import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import TableContainer from "../../../../components/Common/TableContainer";
import { urlDecryptData } from "helpers/crypto_helper";
import { useLocation, useHistory } from "react-router-dom";
import CustomMonthPicker from "common/CustomMonthPicker";
import { useDispatch, useSelector } from "react-redux";
import { generateTimesheetHRApprovalRequest, resetTimesheetHRApprovals, saveGeneratedTimesheetHRApprovalRequest } from "store/actions";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { format, eachDayOfInterval, endOfMonth, startOfMonth } from "date-fns";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";
import { exportEmployeeToExcel } from "pages/exports/exportExcel";
import { exportEmployeeToPDF } from "pages/exports/exportPDF";

const getDateColumns = (selectedMonth) => {
  if (!selectedMonth) return [];

  const start = startOfMonth(new Date(selectedMonth));
  const end = endOfMonth(new Date(selectedMonth));
  const days = eachDayOfInterval({ start, end });

  return days.map((date) => {
    const formattedKey = format(date, "dd.MM.yy");

    return {
      Header: formattedKey,
      accessor: row => row[formattedKey],  // <-- key fix here!
      id: formattedKey,                    // <-- needed when using accessor function
      Cell: ({ value }) => (
        <div className="text-center">{value || "-"}</div>
      ),
      className: "bg-date",
      applyHeaderClass: true,
    };
  });
};

const TimeSheetApprovalGenerate = () => {
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);
  const encryptedData = query.get("id");
  const decryptedData = encryptedData ? urlDecryptData(encryptedData) : null;
  const [timesheetFilterDetails, setTimesheetFilterDetails] = useState(JSON.parse(decryptedData));
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);
  const {
    generatehrApprovals,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    generatehrApprovals: state.TimesheetTransactionApprovalReducer?.generatehrApprovals,

    loading: state.TimesheetTransactionApprovalReducer?.loading,
    error: state.TimesheetTransactionApprovalReducer?.error,
    success: state.TimesheetTransactionApprovalReducer?.success
  }));
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [timesheetHRApprovalData, setTimesheetHRApprovalData] = useState();
  useEffect(() => {
    if (generatehrApprovals && generatehrApprovals.length > 0) {
      const initializedData = (generatehrApprovals || []).map(row => ({
        ...row,
        selectedOption: null,
        remarks: "",
        approved_hours: row.total_approved_hours,
      }));
      setTimesheetHRApprovalData(initializedData)
    } else {
      setTimesheetHRApprovalData([])
      dispatch(resetTimesheetHRApprovals())
    }
  }, [generatehrApprovals])

  const formik = useFormik({
    initialValues: {
      month: "",
      noOfDays: "",
    },
    validationSchema: Yup.object({
      month: Yup.string().required("Month is required"),
      noOfDays: Yup.string().required("Number of Days is required"),
    }),
    onSubmit: (values) => {
      console.log('values')
    },
  });

  const fetchGenerateDetails = (userdetails) => {

    const unit_id = userdetails.unit_id
    const user_id = userdetails.user_id
    // Extract each value individually
    const category_id = timesheetFilterDetails.category?.length
      ? timesheetFilterDetails.category.map(item => item.value).join(",")
      : "0";

    const department_id = timesheetFilterDetails.department?.length
      ? timesheetFilterDetails.department.map(item => item.value).join(",")
      : "0";

    const designation_id = timesheetFilterDetails.designation?.length
      ? timesheetFilterDetails.designation.map(item => item.value).join(",")
      : "0";

    const month = timesheetFilterDetails?.month || "";
    const from_date = "";
    const to_date = "";
    const emp_id = timesheetFilterDetails.employee?.value || "0";

    // Append to FormData
    const formData = new FormData();
    formData.append("category_id", category_id);
    formData.append("department_id", department_id);
    formData.append("designation_id", designation_id);
    formData.append("month", month);
    formData.append("from_date", from_date);
    formData.append("to_date", to_date);
    formData.append("emp_id", emp_id);
    formData.append("unit_id", unit_id);
    formData.append("create_by", user_id);

    // Dispatch the API call
    dispatch(generateTimesheetHRApprovalRequest(formData));
  };

  useEffect(() => {
    if (timesheetFilterDetails?.month && timesheetFilterDetails?.noOfDays !== undefined) {
      formik.setValues({
        month: timesheetFilterDetails.month,
        noOfDays: timesheetFilterDetails.noOfDays.toString(),
      });
      const authUserDetails = useAuthUserDetails();
      setAuthDetails(authUserDetails[0])
      fetchGenerateDetails(authUserDetails[0]);
    }
  }, [timesheetFilterDetails, dispatch]);

  useEffect(() => {
    if (success) {
      fetchGenerateDetails(authDetails);
    }
  }, [authDetails, success, dispatch]);


  const handleRemarksChange = (sno, value) => {
    const updatedData = (timesheetHRApprovalData || []).map((item) =>
      item.emp_id === sno ? { ...item, remarks: value } : item
    );
    setTimesheetHRApprovalData(updatedData);
  };

  const formatTimeWithColon = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0,)}:${digits.slice(2)}`;
  };

  const timeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleHourChange = (value, rowIndex) => {
    let input = value.trim();

    // Handle input without colon
    if (!input.includes(":")) {
      const digitsOnly = input.replace(/\D/g, "");
      if (digitsOnly.length === 1) {
        input = `0${digitsOnly}:00`; // "3" → "03:00"
      } else if (digitsOnly.length === 2) {
        input = `${digitsOnly}:00`; // "12" → "12:00"
      }
    }

    // const formatted = formatTimeWithColon(input); // Force to HH:MM
    const formatted = input; // Force to HH:MM
    const updated = [...timesheetHRApprovalData];

    const approvedMinutes = timeToMinutes(formatted);
    const workedMinutes = timeToMinutes(updated[rowIndex]?.total_approved_hours || "00:00");

    // if (approvedMinutes > workedMinutes) {
    //   Swal.fire({
    //     icon: 'warning',
    //     title: 'Invalid Entry',
    //     text: 'Approved hours cannot be greater than total approved hours.',
    //   });
    //   updated[rowIndex].approved_hours = updated[rowIndex].total_approved_hours;
    // } else {
    updated[rowIndex].approved_hours = formatted;
    // }

    setTimesheetHRApprovalData(updated); // Ensure update reflects globally
  };

  const columns = useMemo(() => {
    const staticColumns = [


      // { Header: "Code", accessor: "emp_no" },
      // { Header: "Name", accessor: "emp_name" },
      // { Header: "Month", accessor: "salary_month" },
      {
        Header: "EMPLOYEE HOURS",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            id: "selection",
            className: "serial-no",
            applyHeaderClass: true,
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div className="text-center">
                <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            Cell: ({ row }) => (
              <div className="text-center">
                <input type="checkbox" {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          {
            Header: "Approve Hrs",
            // accessor: "total_approved_hours",
            className: "bg-approve",
            applyHeaderClass: true,
            Cell: ({ row }) => {
              const rowIndex = row.index;
              const [value, setValue] = useState(timesheetHRApprovalData[rowIndex]?.approved_hours || "");

              const handleChange = (e) => {
                const input = e.target.value;

                // Allow only digits and one colon
                if (!/^[0-9:]*$/.test(input)) return;

                // Only one colon allowed
                if ((input.match(/:/g) || []).length > 1) return;

                // Split input into hours and minutes
                const [hours, minutes] = input.split(":");

                if (hours?.length > 3) return;
                if (minutes?.length > 2) return;

                setValue(input);
              };

              const handleBlur = () => {
                const regex = /^(\d{1,3}):([0-5][0-9])$/;

                if (regex.test(value)) {
                  handleHourChange(value, rowIndex);
                } else {
                  Swal.fire({
                    icon: "error",
                    title: "Invalid Time Format",
                    text: "Please enter time in HHH:MM format.\nHours (max 3 digits) and Minutes (00–59).",
                    confirmButtonColor: "#d33"
                  });
                  setValue("");
                }
              };

              return (
                <Input
                  type="text"
                  value={value}
                  // onChange={(e) => setValue(e.target.value)}
                  // onBlur={(e) => {
                  //   const formatted = e.target.value;
                  //   setValue(formatted);
                  //   handleHourChange(formatted, rowIndex); 
                  // }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="HH:MM"
                  style={{ width: "80px", padding: "4px" }}
                />
              );
            },
          },
          {
            Header: "Remarks",
            // accessor: "remarks",
            className: "bg-approve",
            applyHeaderClass: true,
            Cell: ({ row }) => {
              const rowIndex = row.index;
              const [value, setValue] = useState(timesheetHRApprovalData[rowIndex]?.remarks || "");

              const handleBlur = () => {
                if (value) {
                  handleRemarksChange(row.original.emp_id, value);
                }
              };

              return (
                <Input
                  type="text"
                  bsSize="sm"
                  placeholder="Enter remarks"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onBlur={handleBlur} // only update global state on focus out
                />
              );
            },
          },
          {
            Header: "Code",
            accessor: "emp_no",
            applyHeaderClass: true,
          },
          {
            Header: "Name",
            accessor: "emp_name",
            applyHeaderClass: true,
          },
          {
            Header: "Month",
            accessor: "salary_month",
            applyHeaderClass: true,
          },
        ],
      },
      {
        Header: "ATTENDNACE",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          { Header: "Working Days", applyHeaderClass: true, className: "bg-leave", accessor: "total_days", className: "bg-leave text-center", },
          {
            Header: "Present",
            accessor: "present",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Leave",
            accessor: "leave_days",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Week Off",
            accessor: "week_off",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Holiday",
            accessor: "holiday",
            className: "bg-leave text-center",
            applyHeaderClass: true,
          },
        ],
      },
      {
        Header: "PROJECT",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            Header: "Billable",
            accessor: "billable_hours",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Non Billable",
            accessor: "non_billable_hours",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
        ],
      },
      {
        Header: "NON PROJECT",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            Header: "AMC",
            accessor: "amc_hours",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Pre Sales",
            accessor: "pre_sales",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Billable",
            accessor: "non_projects_billable",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
          {
            Header: "Non Billable",
            accessor: "non_project_non_billable",
            className: "bg-nonprjt text-center",
            applyHeaderClass: true,
          },
        ],
      },
      {
        Header: "TOTAL",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            Header: "ACTUAL HOURS",
            accessor: "shift_minutes",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
          {
            Header: "WORKED HOURS",
            accessor: "total_approved_hours",
            className: "bg-billable text-center",
            applyHeaderClass: true,
          },
        ],
      },
      // {
      //   Header: "TOTAL",
      //   accessor: "total_worked_hours",
      //   className: "bg-total text-center",
      //   applyHeaderClass: true,
      // },
      {
        Header: "APPROVED",
        applyHeaderClass: true,
        className: "bg-project text-center",
        columns: [
          {
            Header: "HOD",
            accessor: "manager_approved_hours",
            className: "bg-worked text-center",
            applyHeaderClass: true,
          },
          {
            Header: "HR",
            accessor: "hr_approved_hours",
            className: "bg-worked text-center",
            applyHeaderClass: true,
          },
        ],
      },
    ];

    const dynamicDateCols = getDateColumns(timesheetFilterDetails?.month);
    return [...staticColumns, ...dynamicDateCols];
  }, [timesheetHRApprovalData, timesheetFilterDetails?.month]);

  const handleCancel = () => {
    history.push(`/time-trans-hr-appr`);
  }

  const handleSelectedRowsChange = React.useCallback((rows) => {
    setSelectedRows(rows);
  }, []);

  const handleApprove = () => {
    if (!selectedRows || selectedRows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No records selected',
        text: 'Please select at least one employee to approve timesheet hours.'
      });
      return;
    }
    const formatted = {
      timesheet: selectedRows.map(row => ({
        emp_id: String(row.emp_id),
        approval_status: "1",
        approval_hours: row.approved_hours,
        approval_remarks: row.remarks
      }))
    };
    const formData = new FormData();
    formData.append('lst_timesheet_hrapproval', JSON.stringify(formatted))
    formData.append('create_by', authDetails?.user_id)
    formData.append('unit_id', authDetails?.unit_id)
    dispatch(saveGeneratedTimesheetHRApprovalRequest(formData))
  }

  return (
    <div className="page-content">
      <Loader loading={loading} />
      <Container fluid>
        <Breadcrumbs title="Time Sheet" breadcrumbItem="HR Approval" />

        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row >
                <Col md={3}>
                  <FormGroup>
                    <Label>Month <span className="text-danger">*</span></Label>
                    <CustomMonthPicker
                      name="month"
                      value={formik.values.month}
                      onChange={(date) => formik.setFieldValue("month", date)}
                      onBlur={formik.handleBlur}
                      disableFuture={true}
                      className={`form-control ${formik.touched.month && formik.errors.month ? "is-invalid" : ""}`}
                      disabled
                    />
                    {formik.touched.month && formik.errors.month && (
                      <div className="text-danger">{formik.errors.month}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>No of Days <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="noOfDays"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.noOfDays}
                      className={formik.errors.noOfDays && formik.touched.noOfDays ? "is-invalid" : ""}
                      disabled
                    />
                  </FormGroup>
                </Col>

                <Col className="d-flex align-items-center justify-content-end">
                  <Button type="button" className="btn button-save me-2" onClick={handleApprove}>
                    <i className="fas fa-check-circle me-1" /> Approve
                  </Button>
                  <Button type="reset" className="btn button-cancel me-2" onClick={handleCancel}>
                    <i className="fas fa-sync me-1" /> Cancel
                  </Button>
                  {timesheetHRApprovalData && timesheetHRApprovalData.length > 0 && (
                    <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down">
                      <DropdownToggle color="primary" className="btn button-export me-2 ms-2"> <i className="fas fa-file-export me-1" /> Export</DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem onClick={() => exportEmployeeToExcel(timesheetHRApprovalData, authDetails?.unit_fullname, authDetails?.unit_address, "Timesheet Approval Report")}>
                          <i className="fas fa-file-excel me-2 text-success" /> Export to Excel
                        </DropdownItem>
                        <DropdownItem onClick={() => exportEmployeeToPDF(timesheetHRApprovalData, authDetails?.unit_fullname, authDetails?.unit_address, "Timesheet Approval Report", columns)}>
                          <i className="fas fa-file-pdf me-2 text-danger" /> Export to PDF
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        <Card>
          <CardBody style={{ overflowX: "auto" }}>
            <TableContainer
              columns={columns}
              data={timesheetHRApprovalData && timesheetHRApprovalData.length > 0 ? timesheetHRApprovalData : []}
              onSelectedRowsChange={handleSelectedRowsChange}
              customPageSize={10}
              isGlobalFilter={true}
              className="custom-header-css"
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TimeSheetApprovalGenerate;